const QA_LANE_PATTERN = /^(qa\d{1,4}|test)$/;

/**
 * True when `value` names one of Otto's dev environments — a numbered QA lane
 * (`qa220`) or the standing dev environment (`test`).
 *
 * Shared deliberately, same reasoning as `isSafeRelativePath`: this is the guard
 * that keeps the auth-link `qa_lane` parameter from becoming an open redirect,
 * and it runs on both tiers. clinic-web validates the search param before
 * forwarding it, and `provisionUser` re-validates server side before a host is
 * built from it. Two copies of a security predicate drift.
 *
 * A lane name is taken instead of a host so no caller-supplied host ever reaches
 * the redirect. The only thing that crosses the wire is the label; the host is
 * assembled here from a fixed template, so the worst a hostile value can do is
 * fail this check.
 *
 * This module intentionally has **no imports**, so it is safe to pull into a
 * browser bundle via its subpath export — same pattern as `./relative-path`.
 */
export function isQaLane(value: string): boolean {
  return QA_LANE_PATTERN.test(value);
}

/**
 * Flow's host for a dev lane. Throws on anything `isQaLane` rejects, so a bad
 * value cannot quietly become a host.
 *
 * `flowDomain` comes from the deployment rather than a literal here, and only the
 * dev overlays supply it. That is the control that keeps a production pod from
 * ever building a dev host: no domain, no lane redirect.
 */
export function buildQaLaneFlowUrl(lane: string, flowDomain: string): string {
  if (!isQaLane(lane)) {
    throw new Error(`Not an Otto dev lane: ${lane}`);
  }

  return `https://flow.${lane}.${flowDomain}`;
}
